import cn from 'classnames';
import { useIntl } from 'react-intl';
import { Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { SettingsTooltip } from '../SettingsTooltip';
import { AlarmItemStyled } from './AlarmItem.styles';

import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { Notifications } from '../types';

type Props = {
  alarm: Notifications['alarms'][0] & {
    description: string;
    name: string;
  };
  alarms: Notifications['alarms'];
  notificationsMailingPermission: boolean;
  onAddAlarm: (alarm: Notifications['alarms'][0]) => void;
  onSetAlarm: (alarm: Notifications['alarms'][0]) => void;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
};

function AlarmItem({
  alarm,
  alarms: userAlarms,
  notificationsMailingPermission,
  onAddAlarm,
  onSetAlarm,
  user,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();

  const handleAlarmEnabledClick = (
    alarmClicked: Notifications['alarms'][0] = {
      enabled: false,
      subscribed: false
    }
  ) => {
    const userAlarm = userAlarms.find(a => a.type === alarmClicked.type);
    if (!userAlarm) {
      onAddAlarm({
        enabled: !alarmClicked.enabled,
        type: alarmClicked.type
      });
    } else {
      const alarmConfig: Notifications['alarms'][0] = {
        enabled: !userAlarm.enabled,
        type: alarmClicked.type
      };
      if (userAlarm.enabled) {
        alarmConfig.subscribed = false;
      }
      onSetAlarm(alarmConfig);
    }
  };

  const handleAlarmSubscriptionClick = (
    alarmClicked: Notifications['alarms'][0] = {
      enabled: false,
      subscribed: false
    }
  ) => {
    const userAlarm = userAlarms.find(a => a.type === alarmClicked.type);
    if (!userAlarm) {
      onAddAlarm({
        enabled: !alarmClicked.enabled,
        subscribed: !alarmClicked.subscribed,
        type: alarmClicked.type
      });
    } else {
      const alarmConfig: Notifications['alarms'][0] = {
        subscribed: !userAlarm.subscribed,
        type: alarmClicked.type
      };
      if (!userAlarm.subscribed) {
        alarmConfig.enabled = true;
      }
      onSetAlarm(alarmConfig);
    }
  };

  return (
    <AlarmItemStyled className="alarmItem" data-alarm-type={alarm.type}>
      <div className="alarmItem__itemHeaderColumn toggle">
        <div
          className={cn('alarmToggle button tiny radius', {
            disabledButton: !alarm.enabled,
            enabledButton: alarm.enabled
          })}
          onClick={() => handleAlarmEnabledClick(alarm)}
        >
          <span
            className={cn({ disabledAlarm: !alarm.enabled, enabledAlarm: alarm.enabled })}
            title={formatMessage({
              defaultMessage: 'Alarm',
              id: `notificationSettings.alarm${alarm.enabled ? 'Enabled' : 'Disabled'}`
            })}
          >
            <i className={cn('fa', { 'fa-check': alarm.enabled, 'fa-times': !alarm.enabled })} />
            {formatMessage({
              defaultMessage: 'Alarm',
              id: `notificationSettings.alarmIs${alarm.enabled ? 'Enabled' : 'Disabled'}`
            })}
          </span>
        </div>
      </div>
      <div className="alarmItem__itemHeaderColumn name" data-sort-by="name">
        {alarm.name}
      </div>
      <div className="alarmItem__itemHeaderColumn description">{`${alarm?.description}.`}</div>
      <div className="alarmItem__itemHeaderColumn settings text-center">
        <Tooltip
          config={{
            arrow: false,
            component: (
              <div className="customTooltip alarmSettingsTooltip" id={alarm.type}>
                <SettingsTooltip
                  alarm={alarm}
                  alarms={userAlarms}
                  onAddAlarm={onAddAlarm}
                  onSetAlarm={onSetAlarm}
                  user={user}
                  userRoles={userRoles}
                />
              </div>
            ),
            forcePlacement: true,
            interactions: [TooltipInteractionEnum.click],
            placement: 'bottom',
            portal: false,
            strategy: 'absolute'
          }}
        >
          <i className="fas fa-sliders-h alarmSettingsToggle" />
        </Tooltip>
      </div>
      {notificationsMailingPermission && (
        <div className="alarmItem__itemHeaderColumn subscription text-center">
          <div
            className={cn({
              subscribedsubscribedButton: alarm.subscribed,
              unsubscribedsubscribedButton: !alarm.subscribed
            })}
          >
            <i
              className={cn('alarmSubscriptionToggle fas', {
                'fa-toggle-off': !alarm.subscribed,
                'fa-toggle-on': alarm.subscribed,
                subscribed: alarm.subscribed,
                unsubscribed: !alarm.subscribed
              })}
              onClick={() => handleAlarmSubscriptionClick(alarm)}
              title={formatMessage({
                defaultMessage: '',
                id: `notificationSettings.alarmSubscription${alarm.subscribed ? 'Enabled' : 'Disabled'}`
              })}
            />
          </div>
        </div>
      )}
    </AlarmItemStyled>
  );
}

export default AlarmItem;
